import React, { useEffect } from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { useEventTracking } from '@services/statsig';

import type { Appointment } from '../types';
import {
  appointmentHasRecordedEncounter,
  getEmbeddedEncounter,
  getLinkedEncounterRecordingCount,
} from '../utils/recordedEncounter';

export interface DeleteAppointmentConfirmDialogProps {
  open: boolean;
  appointment: Appointment;
  source: 'dashboard' | 'schedule' | 'appointment_collision' | 'appointment_edit';
  onClose: () => void;
  onConfirm: () => void;
  /** Copy for the no-recorded-content case. The recorded-content warning copy is fixed. */
  title?: string;
  message?: string;
  confirmText?: string;
}

const RECORDED_TITLE = 'Delete Appointment With a Recorded Session?';
const RECORDED_MESSAGE =
  'This appointment is linked to a session that already has a recording. The recording ' +
  'and its note will be kept, but deleting the appointment disconnects them from your ' +
  'schedule and they can be hard to find afterward. Are you sure you want to delete it?';

/**
 * Confirmation dialog for deleting a single appointment (GDN-2325). When the
 * linked encounter already has recorded content, the copy escalates to make
 * the consequence explicit: the backend soft-deletes the appointment only, so
 * the encounter and its recording are kept but orphaned from the schedule.
 */
export const DeleteAppointmentConfirmDialog: React.FC<DeleteAppointmentConfirmDialogProps> = ({
  open,
  appointment,
  source,
  onClose,
  onConfirm,
  title = 'Delete Appointment?',
  message = 'Are you sure you want to delete this appointment? This cannot be undone.',
  confirmText = 'Delete',
}) => {
  const { appointment: appointmentTracking } = useEventTracking();
  const hasRecordedEncounter = appointmentHasRecordedEncounter(appointment);

  const trackingMetadata = {
    appointment_id: appointment.appointment_id,
    encounter_id: getEmbeddedEncounter(appointment)?.encounter_id ?? undefined,
    recording_count: getLinkedEncounterRecordingCount(appointment),
    source,
  };

  useEffect(() => {
    if (open && hasRecordedEncounter) {
      appointmentTracking.deleteRecordingWarningOpened(trackingMetadata);
    }
    // Fire once per dialog open; metadata is derived from the same appointment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasRecordedEncounter]);

  const handleClose = () => {
    if (hasRecordedEncounter) {
      appointmentTracking.deleteRecordingWarningCancelClicked(trackingMetadata);
    }
    onClose();
  };

  const handleConfirm = () => {
    if (hasRecordedEncounter) {
      appointmentTracking.deleteRecordingWarningConfirmClicked(trackingMetadata);
    }
    onConfirm();
  };

  return (
    <ConfirmDialog
      open={open}
      variant={hasRecordedEncounter ? 'error' : 'warning'}
      title={hasRecordedEncounter ? RECORDED_TITLE : title}
      message={hasRecordedEncounter ? RECORDED_MESSAGE : message}
      onClose={handleClose}
      onConfirm={handleConfirm}
      confirmText={hasRecordedEncounter ? 'Delete Anyway' : confirmText}
    />
  );
};
